// src/pages/EvaluationPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/ApiService';
import logoImage from '../assets/logo.png';
import '../styles/evaluation.css';
import SEOHead from '../components/SEO/SEOHead';

/**
 * EvaluationPage - Questionnaire post-livraison (Wizard)
 * 
 * Accessible via : /evaluation/:idCommande
 * 
 * États possibles :
 * - loading : Chargement des données
 * - not_found : Commande inexistante
 * - not_delivered : Commande pas encore livrée
 * - already_rated : Déjà évaluée (affichage lecture seule)
 * - form : Formulaire d'évaluation (wizard)
 * - confirmation : Confirmation après soumission
 */

// Icône étoile SVG
const StarIcon = ({ filled, hovered }) => (
  <svg 
    className={`star-icon ${filled ? 'filled' : 'empty'} ${hovered ? 'hovered' : ''}`}
    viewBox="0 0 24 24" 
    fill={filled || hovered ? 'currentColor' : 'none'}
    stroke="currentColor" 
    strokeWidth="1.5"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Icône check pour confirmation
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Configuration des questions
const QUESTIONS = [
  {
    id: 'satisfaction',
    icon: '📦',
    title: 'Satisfaction globale',
    description: 'Dans l\'ensemble, êtes-vous satisfait de cette livraison ?',
    type: 'stars',
    labelLeft: 'Très insatisfait',
    labelRight: 'Excellent'
  },
  {
    id: 'delai',
    icon: '⏱️',
    title: 'Délai de livraison',
    description: 'Le délai de livraison vous a-t-il semblé acceptable ?',
    type: 'stars',
    labelLeft: 'Beaucoup trop long',
    labelRight: 'Très rapide'
  },
  {
    id: 'comportement',
    icon: '🤝',
    title: 'Comportement du livreur',
    description: 'Vous êtes-vous senti à l\'aise et respecté par le livreur lors de la livraison ?',
    type: 'stars',
    labelLeft: 'Pas du tout',
    labelRight: 'Tout à fait'
  },
  {
    id: 'communication',
    icon: '📞',
    title: 'Communication avec le livreur',
    description: 'Comment s\'est passée la communication avec le livreur (appel ou échange) ?',
    type: 'stars',
    labelLeft: 'Très mauvaise',
    labelRight: 'Excellente'
  },
  {
    id: 'remise',
    icon: '✅',
    title: 'Remise de la commande',
    description: 'La remise de votre commande s\'est-elle faite sans difficulté ?',
    type: 'yesno'
  },
  {
    id: 'commentaire',
    icon: '💬',
    title: 'Votre avis compte !',
    description: 'Un commentaire ou une suggestion à nous laisser ?',
    type: 'comment'
  }
];

// Emojis selon la note
const RATING_EMOJIS = ['', '😞', '😐', '🙂', '😊', '🤩'];

const EvaluationPage = () => {
  const { idCommande } = useParams();
  const navigate = useNavigate();

  // États
  const [pageState, setPageState] = useState('loading'); // loading, not_found, not_delivered, already_rated, form, confirmation
  const [orderInfo, setOrderInfo] = useState(null);
  const [existingEvaluation, setExistingEvaluation] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  
  // Réponses
  const [answers, setAnswers] = useState({
    satisfaction: 0,
    delai: 0,
    comportement: 0,
    communication: 0,
    remise: null,
    commentaire: ''
  });

  // Chargement initial
  useEffect(() => {
    loadEvaluationData();
  }, [idCommande]);

  const loadEvaluationData = async () => {
    try {
      setPageState('loading');
      
      const response = await ApiService.fetchWithErrorHandling(`/evaluations/index.php?id_commande=${idCommande}`);
      
      if (!response.success) {
        if (response.error === 'commande_not_found') {
          setPageState('not_found');
        } else if (response.error === 'not_delivered') {
          setPageState('not_delivered');
          setOrderInfo(response.order_info || null);
        } else {
          setPageState('not_found');
        }
        return;
      }

      setOrderInfo(response.order_info);

      if (response.already_rated) {
        setExistingEvaluation(response.evaluation);
        setPageState('already_rated');
      } else {
        setPageState('form');
      }
    } catch (error) {
      // console.error('Erreur chargement évaluation:', error);
      setPageState('not_found');
    }
  };

  // Navigation entre étapes
  const goToNextStep = () => {
    if (currentStep < QUESTIONS.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
        setHoveredStar(0);
      }, 300);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
        setHoveredStar(0);
      }, 300);
    }
  };

  // Gestion des réponses
  const handleStarClick = (questionId, rating) => {
    setAnswers(prev => ({ ...prev, [questionId]: rating }));
  };

  const handleYesNoClick = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCommentChange = (e) => {
    const value = e.target.value.slice(0, 500);
    setAnswers(prev => ({ ...prev, commentaire: value }));
  };

  // Validation de l'étape courante
  const isCurrentStepValid = () => {
    const question = QUESTIONS[currentStep];
    if (question.type === 'stars') {
      return answers[question.id] > 0;
    } else if (question.type === 'yesno') {
      return answers[question.id] !== null;
    }
    return true; // Le commentaire est optionnel
  };

  // Calcul de la note globale
  const calculateGlobalScore = () => {
    const remiseNote = answers.remise ? 5 : 1;
    const total = answers.satisfaction + answers.delai + answers.comportement + answers.communication + remiseNote;
    return (total / 5).toFixed(2);
  };

  // Soumission
  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = {
        id_commande: parseInt(idCommande),
        note_satisfaction: answers.satisfaction,
        note_delai: answers.delai,
        note_comportement: answers.comportement,
        note_communication: answers.communication,
        remise_ok: answers.remise ? 1 : 0,
        note_globale: parseFloat(calculateGlobalScore()),
        commentaire: answers.commentaire.trim() || null
      };

      const response = await ApiService.fetchWithErrorHandling('/evaluations/index.php', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        setPageState('confirmation');
      } else {
        alert(response.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      // console.error('Erreur soumission:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirection vers l'accueil
  const goToHome = () => {
    navigate('/');
  };

  // Rendu étoiles interactives
  const renderStars = (questionId, currentRating) => {
    return (
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className="star-button"
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            onClick={() => handleStarClick(questionId, star)}
          >
            <StarIcon 
              filled={star <= currentRating} 
              hovered={hoveredStar > 0 && star <= hoveredStar && star > currentRating}
            />
          </button>
        ))}
      </div>
    );
  };

  // Rendu boutons Oui/Non
  const renderYesNo = (questionId, currentValue) => {
    return (
      <div className="yesno-container">
        <button
          type="button"
          className={`yesno-button yes ${currentValue === true ? 'selected' : ''}`}
          onClick={() => handleYesNoClick(questionId, true)}
        >
          <span className="yesno-icon">👍</span>
          <span>Oui</span>
        </button>
        <button
          type="button"
          className={`yesno-button no ${currentValue === false ? 'selected' : ''}`}
          onClick={() => handleYesNoClick(questionId, false)}
        >
          <span className="yesno-icon">👎</span>
          <span>Non</span>
        </button>
      </div>
    );
  };

  // Rendu zone de commentaire
  const renderComment = () => {
    return (
      <div className="comment-container">
        <label className="comment-label">
          💬 Votre message
          <span className="optional-badge">Optionnel</span>
        </label>
        <textarea
          className="comment-textarea"
          placeholder="Partagez votre expérience avec nous... Qu'avez-vous apprécié ? Que pouvons-nous améliorer ?"
          value={answers.commentaire}
          onChange={handleCommentChange}
          maxLength={500}
        />
        <div className="comment-counter">{answers.commentaire.length}/500</div>
      </div>
    );
  };

  // Rendu carte de question
  const renderQuestionCard = () => {
    const question = QUESTIONS[currentStep];
    const displayedRating = hoveredStar > 0 ? hoveredStar : answers[question.id];

    return (
      <div className={`question-card ${isAnimating ? 'slide-out' : ''}`}>
        <span className="question-icon">{question.icon}</span>
        <span className="question-number">Question {currentStep + 1} sur {QUESTIONS.length}</span>
        <h2 className="question-title">{question.title}</h2>
        <p className="question-description">{question.description}</p>

        {question.type === 'stars' && (
          <>
            {renderStars(question.id, answers[question.id])}
            <div className="stars-labels">
              <span className="star-label">{question.labelLeft}</span>
              <span className="star-label">{question.labelRight}</span>
            </div>
            <span className={`rating-emoji ${displayedRating > 0 ? 'visible' : ''}`}>
              {RATING_EMOJIS[displayedRating] || ''}
            </span>
          </>
        )}

        {question.type === 'yesno' && renderYesNo(question.id, answers[question.id])}

        {question.type === 'comment' && renderComment()}
      </div>
    );
  };

  // Rendu barre de progression
  const renderProgressBar = () => {
    const progress = ((currentStep + 1) / QUESTIONS.length) * 100;

    return (
      <div className="progress-container">
        <div className="progress-text">
          <span>Votre évaluation</span>
          <span className="progress-step">Étape {currentStep + 1}/{QUESTIONS.length}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="progress-dots">
          {QUESTIONS.map((_, index) => (
            <div 
              key={index}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>
    );
  };

  // Rendu boutons de navigation
  const renderNavigationButtons = () => {
    const isLastStep = currentStep === QUESTIONS.length - 1;
    const canProceed = isCurrentStepValid();

    return (
      <div className="navigation-buttons">
        {currentStep > 0 && (
          <button 
            type="button" 
            className="nav-button secondary"
            onClick={goToPrevStep}
            disabled={isAnimating}
          >
            ← Retour
          </button>
        )}
        
        {!isLastStep ? (
          <button 
            type="button" 
            className="nav-button primary"
            onClick={goToNextStep}
            disabled={!canProceed || isAnimating}
          >
            Suivant →
          </button>
        ) : (
          <button 
            type="button" 
            className="nav-button primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Envoi...' : 'Envoyer mon évaluation'}
          </button>
        )}
      </div>
    );
  };

  // Rendu page de confirmation
  const renderConfirmation = () => {
    const globalScore = calculateGlobalScore();
    const fullStars = Math.floor(globalScore);
    const hasHalfStar = globalScore - fullStars >= 0.5;

    return (
      <div className="confirmation-card">
        <div className="confirmation-icon">
          <CheckIcon />
        </div>
        <h1 className="confirmation-title">Merci pour votre évaluation !</h1>
        <p className="confirmation-message">
          Votre avis nous aide à améliorer notre service de livraison.
        </p>

        <div className="rating-summary">
          <div className="rating-summary-title">Note attribuée</div>
          <div className="rating-summary-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon key={star} filled={star <= fullStars || (star === fullStars + 1 && hasHalfStar)} />
            ))}
          </div>
          <div className="rating-summary-score">
            {globalScore}<span>/5</span>
          </div>
        </div>

        <button type="button" className="confirmation-button" onClick={goToHome}>
          Commander à nouveau
        </button>
      </div>
    );
  };

  // Rendu page déjà noté (lecture seule)
  const renderAlreadyRated = () => {
    if (!existingEvaluation) return null;

    const renderStarsReadOnly = (rating) => (
      <div className="rating-detail-value">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg 
            key={star}
            className="mini-star"
            viewBox="0 0 24 24" 
            fill={star <= rating ? 'currentColor' : 'none'}
            stroke="currentColor" 
            strokeWidth="1.5"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );

    return (
      <div className="already-rated-card">
        <div className="already-rated-header">
          <div className="already-rated-icon">✅</div>
          <h1 className="already-rated-title">Évaluation déjà soumise</h1>
          <p className="already-rated-subtitle">
            Vous avez déjà évalué cette commande. Voici votre récapitulatif :
          </p>
        </div>

        <div className="rating-summary">
          <div className="rating-summary-title">Note globale</div>
          <div className="rating-summary-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon key={star} filled={star <= Math.round(existingEvaluation.note_globale)} />
            ))}
          </div>
          <div className="rating-summary-score">
            {parseFloat(existingEvaluation.note_globale).toFixed(1)}<span>/5</span>
          </div>
        </div>

        <div className="rating-details">
          <div className="rating-detail-item">
            <span className="rating-detail-label">📦 Satisfaction globale</span>
            {renderStarsReadOnly(existingEvaluation.note_satisfaction)}
          </div>
          <div className="rating-detail-item">
            <span className="rating-detail-label">⏱️ Délai de livraison</span>
            {renderStarsReadOnly(existingEvaluation.note_delai)}
          </div>
          <div className="rating-detail-item">
            <span className="rating-detail-label">🤝 Comportement livreur</span>
            {renderStarsReadOnly(existingEvaluation.note_comportement)}
          </div>
          <div className="rating-detail-item">
            <span className="rating-detail-label">📞 Communication</span>
            {renderStarsReadOnly(existingEvaluation.note_communication)}
          </div>
          <div className="rating-detail-item">
            <span className="rating-detail-label">✅ Remise sans difficulté</span>
            <span className={`rating-detail-value ${existingEvaluation.remise_ok ? 'yes' : 'no'}`}>
              <span className="value-text">{existingEvaluation.remise_ok ? 'Oui 👍' : 'Non 👎'}</span>
            </span>
          </div>
        </div>

        {existingEvaluation.commentaire && (
          <div className="comment-display">
            <div className="comment-display-label">💬 Votre commentaire</div>
            <p className="comment-display-text">"{existingEvaluation.commentaire}"</p>
          </div>
        )}

        <button type="button" className="confirmation-button" onClick={goToHome}>
          Retour à l'accueil
        </button>
      </div>
    );
  };

  // Rendu état de chargement
  const renderLoading = () => (
    <div className="evaluation-loading">
      <div className="spinner" />
      <p>Chargement...</p>
    </div>
  );

  // Rendu erreur : commande non trouvée
  const renderNotFound = () => (
    <div className="evaluation-error">
      <div className="evaluation-error-icon">🔍</div>
      <h2 className="evaluation-error-title">Commande introuvable</h2>
      <p className="evaluation-error-message">
        Cette commande n'existe pas ou le lien est incorrect.
      </p>
      <button type="button" className="evaluation-error-button" onClick={goToHome}>
        Retour à l'accueil
      </button>
    </div>
  );

  // Rendu erreur : commande pas encore livrée
  const renderNotDelivered = () => (
    <div className="evaluation-error">
      <div className="evaluation-error-icon">📦</div>
      <h2 className="evaluation-error-title">Commande en cours</h2>
      <p className="evaluation-error-message">
        Cette commande n'est pas encore livrée. Vous pourrez l'évaluer une fois la livraison effectuée.
      </p>
      <button type="button" className="evaluation-error-button" onClick={goToHome}>
        Retour à l'accueil
      </button>
    </div>
  );

  // Rendu principal
  return (
    <div className="evaluation-page">
      <div className="evaluation-container">
        {/* Header avec logo */}
        <div className="evaluation-header">
          <img 
            src={logoImage} 
            alt="Boissons du Cameroun" 
            className="evaluation-logo"
          />
          <h1 className="evaluation-title">Évaluez votre livraison</h1>
          <p className="evaluation-subtitle">Votre avis nous aide à nous améliorer</p>
          {orderInfo?.prenom_client && (
            <p className="evaluation-greeting">
              Bonjour {orderInfo.prenom_client}, merci pour votre confiance !
            </p>
          )}
        </div>

        {/* Contenu selon l'état */}
        {pageState === 'loading' && renderLoading()}
        {pageState === 'not_found' && renderNotFound()}
        {pageState === 'not_delivered' && renderNotDelivered()}
        {pageState === 'already_rated' && renderAlreadyRated()}
        {pageState === 'confirmation' && renderConfirmation()}
        
        {pageState === 'form' && (
          <>
            {renderProgressBar()}
            {renderQuestionCard()}
            {renderNavigationButtons()}
          </>
        )}
      </div>
    </div>
  );
};

export default EvaluationPage;